import { CARGO_COUNT_COLORS } from '@/types/cargoCount.types';
import type { CargoCountResult } from '@/types/cargoCount.types';
import { useState } from 'react';

interface CargoCountChartBarProps {
  cargoCount: CargoCountResult;
  max: number;
  last?: boolean;
}

export function CargoCountChartBar({ cargoCount, max, last }: CargoCountChartBarProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const fixedWidthPercent = (cargoCount.fixed / max) * 100;
  const expectedWidthPercent = (cargoCount.expected / max) * 100;
  const averageWidthPercent = (cargoCount.average / max) * 100;

  return (
    <>
      <div className="flex items-center">
        {/* Period Label */}
        <div className="w-[82px] min-w-[82px] flex-shrink-0 whitespace-nowrap pr-2 text-right text-sm font-medium opacity-80 sm:text-base">
          {cargoCount.period}
        </div>

        {/* Bar Chart Wrapper */}
        <div className="bar-chart-wrapper relative flex-1 border-l border-gray-300 dark:border-gray-600">
          <div
            className={`bar-chart-container relative box-content h-10 outline-none ${last ? 'pb-1' : 'pb-1 sm:pb-2'} pt-2 sm:pt-3`}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            tabIndex={0}
          >
            {/* Container for Fixed and Expected bars */}
            <div
              className="bar-item absolute mb-[1px] h-10 w-full"
              style={{ width: 'calc(100% - 12px)' }}
            >
              {/* Fixed Bar */}
              <div className="bar-item bar-fixed absolute z-[2] m-[5px] h-[30px] w-full text-white">
                <div
                  className="progress absolute flex h-full items-center justify-center text-xs font-semibold"
                  style={{
                    backgroundColor: CARGO_COUNT_COLORS.fixed,
                    width: `${fixedWidthPercent}%`,
                  }}
                >
                  {cargoCount.fixed > 0 && <span className="value">{cargoCount.fixed}</span>}
                </div>
              </div>

              {/* Expected Bar */}
              <div className="bar-item bar-expected absolute z-[1] m-[5px] h-[30px] w-full">
                <div
                  className="progress absolute flex h-full items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-900"
                  style={{
                    backgroundColor: CARGO_COUNT_COLORS.expected,
                    width: `${expectedWidthPercent}%`,
                    marginLeft: `${fixedWidthPercent}%`,
                  }}
                >
                  {cargoCount.expected > 0 && <span className="value">{cargoCount.expected}</span>}
                </div>
              </div>
            </div>

            {/* Average Line */}
            <div className="bar-item bar-average absolute z-[3] mb-[1px] h-10 w-full">
              <div
                className="progress absolute h-full border border-dashed"
                style={{
                  width: `${averageWidthPercent}%`,
                  borderColor: CARGO_COUNT_COLORS.average,
                }}
              ></div>
            </div>

            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute left-1/2 top-full z-50 mt-2 min-w-[200px] -translate-x-1/2 transform rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-2 block font-bold text-gray-900 dark:text-white">
                  {cargoCount.period}
                </div>
                <ul className="flex flex-col space-y-2">
                  <li className="flex items-center pl-0">
                    <span
                      className="mr-1 inline-block h-4 w-4 rounded-full"
                      style={{ backgroundColor: CARGO_COUNT_COLORS.fixed }}
                    ></span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Fixed: {cargoCount.fixed}
                    </span>
                  </li>
                  <li className="flex items-center pl-0">
                    <span
                      className="mr-1 inline-block h-4 w-4 rounded-full"
                      style={{ backgroundColor: CARGO_COUNT_COLORS.expected }}
                    ></span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Expected: {cargoCount.expected}
                    </span>
                  </li>
                  <li className="flex items-center pl-0">
                    <span
                      className="mr-1 inline-block h-4 w-4 rounded-full border border-dashed"
                      style={{
                        borderColor: CARGO_COUNT_COLORS.average,
                        backgroundColor: 'transparent',
                      }}
                    ></span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Average: {cargoCount.average}
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media only screen and (max-width: 576px) {
          .bar-chart-container {
            height: 28px !important;
          }
          .bar-chart-container .bar-item {
            height: 28px !important;
          }
          .bar-chart-container .bar-item.bar-expected,
          .bar-chart-container .bar-item.bar-fixed {
            height: 22px !important;
            margin: 3px !important;
          }
        }
      `}</style>
    </>
  );
}
